/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { User } from '../types';
import { Search, Users, UserCheck, UserX, Power, AlertTriangle, ShieldCheck, Tag, Trash2, Key, Lock, Check, PenTool } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import CreateUser from './CreateUser';
import UserSignatureUpdater from './UserSignatureUpdater';

interface GestionUsuariosProps {
  users: User[];
  currentUser: User | null;
  onUpdateUserStatus: (userId: string, newStatus: 'ACTIVO' | 'INACTIVO') => void;
  onUpdateUserPassword: (userId: string, newPassword: string) => void;
  onDeleteUser: (userId: string) => void;
  onUserCreated?: (newUser: User) => void;
  allGlobalUsers?: User[];
  onUpdateUserModules?: (userId: string, newModules: string[]) => void;
  onUpdateUserSignature?: (userId: string, signatureBase64: string) => void;
  isReadOnly?: boolean;
}

export default function GestionUsuarios({ 
  users, 
  currentUser, 
  onUpdateUserStatus, 
  onUpdateUserPassword,
  onDeleteUser, 
  onUserCreated,
  allGlobalUsers,
  onUpdateUserModules,
  onUpdateUserSignature,
  isReadOnly = false
}: GestionUsuariosProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'TODOS' | 'ACTIVO' | 'INACTIVO'>('TODOS');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Temporary roles modifications draft state to require a Confirm/Save action
  const [tempModules, setTempModules] = useState<Record<string, string[]>>({});
  const [saveSuccessUserId, setSaveSuccessUserId] = useState<string | null>(null);

  // Password change states
  const [editingPasswordUserId, setEditingPasswordUserId] = useState<string | null>(null);
  const [newPasswordVal, setNewPasswordVal] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Signature change states
  const [editingSignatureUserId, setEditingSignatureUserId] = useState<string | null>(null);

  // Helper to ensure we have a fallback status if none is set
  const getUserStatus = (user: User): 'ACTIVO' | 'INACTIVO' => {
    return user.estado || 'ACTIVO';
  };

  const canChangePassword = (targetUser: User): { allowed: boolean; reason?: string } => {
    if (!currentUser) return { allowed: false, reason: 'No ha iniciado sesión' };
    
    // Only JUAN.VALENCIA and Director Técnico y de Calidad have the privilege to change passwords
    const isAdminOrDirector = currentUser.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser.cargo === 'Director Técnico y de Calidad';
    if (!isAdminOrDirector) {
      return { allowed: false, reason: 'Solo el superusuario JUAN.VALENCIA o el Director Técnico y de Calidad pueden reestablecer contraseñas.' };
    }
    return { allowed: true };
  };

  const canUpdateSignature = (targetUser: User): boolean => {
    if (!currentUser) return false;
    // Only JUAM.VALENCIA and Director Técnico y de Calidad can update anyone's signature, or any user can update their own
    const isAdminOrDirector = currentUser.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser.cargo === 'Director Técnico y de Calidad';
    if (isAdminOrDirector) return true;
    return currentUser.id === targetUser.id;
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      user.nombreCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.cedula.includes(searchTerm) ||
      user.cargo.toLowerCase().includes(searchTerm.toLowerCase());

    const status = getUserStatus(user);
    const matchesStatus = statusFilter === 'TODOS' || status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Simple statistics
  const totalUsers = users.length;
  const activeCount = users.filter((u) => getUserStatus(u) === 'ACTIVO').length;
  const inactiveCount = users.filter((u) => getUserStatus(u) === 'INACTIVO').length;

  const handleToggleStatus = (user: User) => {
    const isAdminOrDirector = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!currentUser || !isAdminOrDirector) {
      alert('Operación denegada. Solo el superusuario JUAN.VALENCIA o el Director Técnico y de Calidad pueden cambiar el estado de los colaboradores.');
      return;
    }
    const currentStatus = getUserStatus(user);
    const newStatus = currentStatus === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO';

    if (user.id === currentUser.id && newStatus === 'INACTIVO') {
      alert('Por motivos de seguridad, no puedes desactivar tu propia cuenta activa con la que iniciaste sesión.');
      return;
    }

    onUpdateUserStatus(user.id, newStatus);
  };

  return (
    <div className="space-y-8 animate-fade-in" id="gestion-usuarios-container">
      
      {/* Visual Header Grid & Banner */}
      <div className="bg-[#071A36] border border-[#1C4F8A]/50 text-white rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-md">
        <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none">
          <Users className="h-64 w-64 transform translate-x-12 translate-y-12" />
        </div>
        
        <div className="relative z-10 max-w-2xl space-y-3">
          <div className="inline-flex items-center space-x-2 bg-[#1C4F8A]/60 px-3 py-1.5 rounded-full border border-[#1C4F8A] text-xs font-semibold text-sky-300">
            <ShieldCheck className="h-4 w-4 text-sky-300" />
            <span>ADMINISTRACIÓN DE ROLES Y ACCESOS</span>
          </div>
          <h1 className="font-sans font-black text-2xl md:text-3xl tracking-tight text-white leading-none">
            CONTROL DE PERSONAL Y SEGURIDAD
          </h1>
          <p className="text-slate-200 text-xs md:text-sm leading-relaxed">
            Visualización y auditoría física del estado del personal asistencial habilitados en la plataforma. Puede cambiar el estado a INACTIVO para restringir temporalmente accesos o validaciones de calidad.
          </p>
        </div>

        {/* Dynamic State Counters */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mt-8 pt-6 border-t border-slate-800 relative z-10">
          <div className="bg-slate-800/40 p-3 rounded-xl border border-slate-800">
            <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Usuarios</span>
            <span className="block text-xl md:text-2xl font-black text-white mt-1">{totalUsers}</span>
          </div>
          <div className="bg-emerald-950/40 p-3 rounded-xl border border-emerald-900/30">
            <span className="block text-[10px] text-emerald-400 font-bold uppercase tracking-wider flex items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
              Activos
            </span>
            <span className="block text-xl md:text-2xl font-black text-emerald-300 mt-1">{activeCount}</span>
          </div>
          <div className="bg-rose-950/40 p-3 rounded-xl border border-rose-900/30">
            <span className="block text-[10px] text-rose-400 font-bold uppercase tracking-wider flex items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500 mr-1.5" />
              Inactivos
            </span>
            <span className="block text-xl md:text-2xl font-black text-rose-300 mt-1">{inactiveCount}</span>
          </div>
        </div>
      </div>

      {isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3 text-amber-900 text-xs font-sans font-bold shadow-2xs">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <span>
            🔒 <strong>Modo Consulta Activo:</strong> Licencia vencida. La creación, desactivación, cambio de contraseñas y modificación de permisos de colaboradores están deshabilitados.
          </span>
        </div>
      )}

      {/* REGISTRATION CARD (Visible only to the super-user JUAN.VALENCIA or Director Técnico y de Calidad) */}
      {(currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad') && (
        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-6 space-y-4 animate-fade-in" id="card-registrar-personal">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-slate-100">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <UserCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                <h3 className="font-sans font-black text-slate-900 text-sm md:text-base uppercase tracking-tight">
                  Registrar Nuevo Personal / Usuario
                </h3>
              </div>
              <p className="text-[11px] text-slate-550 font-sans leading-none">
                Cree perfiles específicos, asigne cargos y autorice módulos para el personal.
              </p>
            </div>
            
            <button
              onClick={() => setShowCreateForm(prev => !prev)}
              className={`text-xs font-sans font-extrabold px-4 py-2 rounded-xl border transition-all cursor-pointer ${
                showCreateForm
                  ? 'bg-slate-800 text-white border-slate-800 hover:bg-slate-700'
                  : 'bg-emerald-50 text-emerald-800 border-emerald-200/60 hover:bg-emerald-100'
              }`}
            >
              {showCreateForm ? 'Ocultar Formulario de Registro' : 'Registrar Nuevo Perfil'}
            </button>
          </div>

          <AnimatePresence>
            {showCreateForm && onUserCreated && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pt-2">
                  <CreateUser
                    onUserCreated={(newUser) => {
                      onUserCreated(newUser);
                      // Keep form open for multiple user creation with feedback
                    }}
                    existingUsers={users}
                    currentUser={currentUser}
                    allGlobalUsers={allGlobalUsers}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Main Content Card with Search & Filters */}
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-xs p-6 space-y-6">
        
        {/* Search controls + tabs filter */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center pb-4 border-b border-slate-100">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, usuario, cédula o cargo..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 text-xs rounded-xl focus:outline-hidden focus:ring-2 focus:ring-slate-550/10 focus:border-slate-400 transition-all font-sans text-slate-700"
              id="search-usuarios"
            />
          </div>

          {/* Quick Filters */}
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200/55 self-start md:self-auto shrink-0">
            <button
              onClick={() => setStatusFilter('TODOS')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                statusFilter === 'TODOS'
                  ? 'bg-white text-slate-800 shadow-xs'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Todos ( {totalUsers} )
            </button>
            <button
              onClick={() => setStatusFilter('ACTIVO')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                statusFilter === 'ACTIVO'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-500 hover:text-emerald-600'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusFilter === 'ACTIVO' ? 'bg-white' : 'bg-emerald-500'}`} />
              <span>Activos ( {activeCount} )</span>
            </button>
            <button
              onClick={() => setStatusFilter('INACTIVO')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                statusFilter === 'INACTIVO'
                  ? 'bg-slate-700 text-white shadow-xs'
                  : 'text-slate-500 hover:text-rose-600'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusFilter === 'INACTIVO' ? 'bg-white' : 'bg-slate-400'}`} />
              <span>Inactivos ( {inactiveCount} )</span>
            </button>
          </div>
        </div>

        {/* Users Visual Grid */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-450">
              <Users className="h-6 w-6 text-slate-400" />
            </div>
            <div>
              <h4 className="font-extrabold text-slate-800 text-sm">No se encontraron usuarios</h4>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                No hay coincidencias para la búsqueda o el filtro seleccionado en el sistema de control.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredUsers.map((user) => {
              const status = getUserStatus(user);
              const isActive = status === 'ACTIVO';
              const isSelf = currentUser && user.id === currentUser.id;

              return (
                <div
                  key={user.id}
                  className={`bg-white rounded-2xl border transition-all duration-200 relative overflow-hidden flex flex-col justify-between ${
                    isActive 
                      ? 'border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300' 
                      : 'border-slate-200 bg-slate-50/70 opacity-80'
                  }`}
                  id={`card-user-${user.username}`}
                >
                  {/* Highlight bar base on status */}
                  <div className={`h-1 w-full ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />

                  {/* Body Content */}
                  <div className="p-5 space-y-4 flex-1">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <h4 className="font-sans font-extrabold text-slate-900 text-sm truncate" title={user.nombreCompleto}>
                          {user.nombreCompleto}
                        </h4>
                        <span className="inline-flex items-center text-[10px] text-slate-400 font-mono mt-0.5">
                          @{user.username}
                        </span>
                        {user.firebaseUid && (
                          <div className="mt-1 flex items-center space-x-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[8px] font-black uppercase tracking-tight">
                              Firebase Auth
                            </span>
                            <span className="text-[8px] font-mono text-slate-400 truncate max-w-[80px]" title={user.firebaseUid}>
                              UID: {user.firebaseUid.substring(0, 6)}...
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Status Badges */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide uppercase shadow-2xs shrink-0 ${
                        isActive 
                          ? 'bg-emerald-100 text-emerald-800' 
                          : 'bg-slate-200 text-slate-650'
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`} />
                        {status}
                      </span>
                    </div>

                     {/* Detailed info lines */}
                    <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs font-sans text-slate-600">
                      <div className="flex justify-between">
                        <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Identificación</span>
                        <span className="font-mono font-medium text-slate-800">{user.cedula}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Cargo / Rol</span>
                        <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md text-[11px] max-w-[150px] truncate" title={user.cargo}>
                           {user.cargo}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-1.5 border-t border-slate-100/60 mt-1">
                        <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Firma Digital</span>
                        {user.firma ? (
                          <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-150 rounded-lg p-1 max-w-[140px]">
                            <span className="inline-flex items-center px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[8px] font-black uppercase shrink-0">
                              Sí
                            </span>
                            <div className="h-6 w-16 flex items-center justify-center overflow-hidden bg-white rounded border border-slate-200 p-0.5">
                              <img src={user.firma} alt="Firma" className="max-h-full max-w-full object-contain" referrerPolicy="no-referrer" />
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-150 text-[9px] font-black uppercase shadow-3xs">
                            ⚠ No Cargada
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Permitted Modules list decoration */}
                    <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs">
                      <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block">Módulos Permitidos</span>
                      
                      {(currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad') && !user.esAdmin && !isSelf ? (
                        <div>
                          <div className="grid grid-cols-2 gap-2 mt-1 bg-slate-50/50 p-2.5 rounded-xl border border-slate-150">
                            {[
                              { id: 'registro_ordenes', label: 'Órdenes', color: 'accent-blue-600' },
                              { id: 'disposicion_productos', label: 'Liberación', color: 'accent-emerald-600' },
                              { id: 'dashboard_productividad', label: 'Indicadores', color: 'accent-amber-600' },
                              { id: 'gestion_usuarios', label: 'Seguridad', color: 'accent-purple-600' }
                            ].map((mod) => {
                              const activeModules = tempModules[user.id] !== undefined 
                                ? tempModules[user.id] 
                                : (user.modulosPermitidos || []);
                              const isChecked = activeModules.includes(mod.id);
                              return (
                                <label key={mod.id} className="inline-flex items-center space-x-1.5 cursor-pointer selection:bg-transparent">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      const nextModules = isChecked 
                                        ? activeModules.filter(m => m !== mod.id)
                                        : [...activeModules, mod.id];
                                      setTempModules(prev => ({
                                        ...prev,
                                        [user.id]: nextModules
                                      }));
                                    }}
                                    className={`rounded border-slate-300 h-3.5 w-3.5 cursor-pointer ${mod.color}`}
                                  />
                                  <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">{mod.label}</span>
                                </label>
                              );
                            })}
                          </div>

                          {/* Save & Cancel Buttons for permissions with visual indicator */}
                          {(() => {
                            const draft = tempModules[user.id];
                            const actual = user.modulosPermitidos || [];
                            const isDifferent = draft !== undefined && JSON.stringify([...draft].sort()) !== JSON.stringify([...actual].sort());
                            
                            return (
                              <div className="mt-2.5 flex items-center justify-between gap-2">
                                {isDifferent ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (onUpdateUserModules && draft !== undefined) {
                                        onUpdateUserModules(user.id, draft);
                                        setSaveSuccessUserId(user.id);
                                        setTimeout(() => {
                                          setSaveSuccessUserId(null);
                                        }, 4000);
                                      }
                                    }}
                                    className="inline-flex flex-1 items-center justify-center space-x-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[10px] uppercase rounded-lg shadow-sm hover:shadow transition-all cursor-pointer border border-emerald-700"
                                  >
                                    <span>Guardar Módulos</span>
                                  </button>
                                ) : saveSuccessUserId === user.id ? (
                                  <div className="inline-flex flex-1 items-center justify-center space-x-1 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-extrabold text-[10px] uppercase rounded-lg animate-pulse">
                                    <Check className="h-3 w-3" />
                                    <span>¡Cargado Exitosamente!</span>
                                  </div>
                                ) : (
                                  <div className="text-[9px] font-semibold text-slate-400 italic">
                                    Selecciona casillas de permisos
                                  </div>
                                )}
                                
                                {isDifferent && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTempModules(prev => {
                                        const copy = { ...prev };
                                        delete copy[user.id];
                                        return copy;
                                      });
                                    }}
                                    className="px-2.5 py-1.5 text-[10px] text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all cursor-pointer font-bold border border-slate-200"
                                  >
                                    Cancelar
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {user.esAdmin ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-900 border border-slate-950 text-white font-sans text-[9px] font-extrabold uppercase shadow-3xs">
                              🔑 ACCESO TOTAL ADM
                            </span>
                          ) : user.modulosPermitidos && user.modulosPermitidos.length > 0 ? (
                            user.modulosPermitidos.map((m) => {
                              let label = '';
                              let color = '';
                              if (m === 'registro_ordenes') { label = 'Órdenes'; color = 'bg-blue-50 text-blue-700 border-blue-200/50'; }
                              else if (m === 'disposicion_productos') { label = 'Liberación'; color = 'bg-emerald-50 text-emerald-700 border-emerald-200/50'; }
                              else if (m === 'dashboard_productividad') { label = 'Indicadores'; color = 'bg-amber-50 text-amber-700 border-amber-200/50'; }
                              else if (m === 'gestion_usuarios') { label = 'Seguridad'; color = 'bg-purple-50 text-purple-700 border-purple-200/50'; }
                              return (
                                <span key={m} className={`inline-flex px-1.5 py-0.5 rounded-md border text-[9px] font-black uppercase tracking-wide ${color}`}>
                                  {label}
                                </span>
                              );
                            })
                          ) : (
                            <span className="inline-flex px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-600 border border-rose-200/40 text-[9px] font-bold uppercase tracking-wide">
                              Sin accesos asignados
                            </span>
                          )}
                        </div>
                      )}
                    </div>
 
                     {isSelf && (
                       <div className="bg-amber-50 rounded-lg p-2.5 text-[10px] text-amber-800 font-sans border border-amber-200/50 flex items-start space-x-1.5">
                         <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                         <span>Este es tu usuario actual de sesión activa. No modificable por seguridad.</span>
                       </div>
                     )}

                      {/* Password Security Panel */}
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block flex items-center gap-1">
                            <Lock className="h-3.5 w-3.5 text-slate-400" />
                            Contraseña
                          </span>
                          {canChangePassword(user).allowed ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (editingPasswordUserId === user.id) {
                                  setEditingPasswordUserId(null);
                                  setNewPasswordVal('');
                                  setPasswordError('');
                                  setPasswordSuccess('');
                                } else {
                                  setEditingPasswordUserId(user.id);
                                  setNewPasswordVal(user.username.toLowerCase() === 'admin' ? 'Jvalencia4@' : '');
                                  setPasswordError('');
                                  setPasswordSuccess('');
                                }
                              }}
                              className="text-[10px] text-blue-600 hover:text-blue-800 font-bold bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-0.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                            >
                              <Key className="h-3 w-3" />
                              <span>{editingPasswordUserId === user.id ? 'Cancelar' : 'Reestablecer'}</span>
                            </button>
                          ) : (
                            <span 
                              className="text-[9px] text-slate-450 font-bold bg-slate-100 border border-slate-200/50 px-2 py-0.5 rounded-md max-w-[130px] truncate" 
                              title={canChangePassword(user).reason}
                            >
                              🔑 Restringido
                            </span>
                          )}
                        </div>

                        {editingPasswordUserId === user.id && canChangePassword(user).allowed && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="bg-slate-50 border border-slate-200/65 rounded-xl p-3 space-y-2.5"
                          >
                            <div className="space-y-1">
                              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                                Nueva Contraseña
                              </label>
                              <div className="flex gap-1.5">
                                <input
                                  type="text"
                                  placeholder={user.username.toLowerCase() === 'admin' ? 'Jvalencia4@' : 'Min. 4 caracteres'}
                                  value={newPasswordVal}
                                  onChange={(e) => {
                                    setNewPasswordVal(e.target.value);
                                    setPasswordError('');
                                    setPasswordSuccess('');
                                  }}
                                  className="grow h-8 px-2.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 font-sans text-slate-800 font-semibold"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const cleaned = newPasswordVal.trim();
                                    if (cleaned.length < 4) {
                                      setPasswordError('Debe tener al menos 4 caracteres.');
                                      return;
                                    }
                                    onUpdateUserPassword(user.id, cleaned);
                                    setPasswordSuccess('¡Contraseña actualizada!');
                                    setTimeout(() => {
                                      setEditingPasswordUserId(null);
                                      setNewPasswordVal('');
                                      setPasswordSuccess('');
                                    }, 1800);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-[10px] uppercase h-8 px-3 rounded-lg flex items-center justify-center shrink-0 cursor-pointer transition-colors shadow-3xs"
                                >
                                  Guardar
                                </button>
                              </div>
                            </div>

                            {user.username.toLowerCase() === 'admin' && (
                              <div className="flex gap-1 items-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewPasswordVal('Jvalencia4@');
                                    setPasswordError('');
                                  }}
                                  className="text-[9px] text-emerald-750 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-md hover:bg-emerald-100 font-bold transition-all cursor-pointer"
                                >
                                  Usar Jvalencia4@
                                </button>
                              </div>
                            )}

                            {passwordError && (
                              <span className="text-[9px] font-bold text-rose-600 block leading-tight">
                                ⚠️ {passwordError}
                              </span>
                            )}

                            {passwordSuccess && (
                              <span className="text-[9px] font-black text-emerald-600 block leading-tight flex items-center gap-1">
                                <Check className="h-3 w-3" /> {passwordSuccess}
                              </span>
                            )}
                          </motion.div>
                        )}
                      </div>

                      {/* Signature Security Panel */}
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block flex items-center gap-1">
                            <PenTool className="h-3.5 w-3.5 text-slate-400" />
                            Garantía de Firma
                          </span>
                          {canUpdateSignature(user) ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (editingSignatureUserId === user.id) {
                                  setEditingSignatureUserId(null);
                                } else {
                                  setEditingSignatureUserId(user.id);
                                }
                              }}
                              className="text-[10px] text-emerald-700 hover:text-emerald-900 font-bold bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                            >
                              <PenTool className="h-3 w-3" />
                              <span>{editingSignatureUserId === user.id ? 'Cancelar' : 'Actualizar Firma'}</span>
                            </button>
                          ) : (
                            <span 
                              className="text-[9px] text-slate-450 font-bold bg-slate-100 border border-slate-200/50 px-2 py-0.5 rounded-md max-w-[130px] truncate"
                              title="No tienes permisos para cambiar firmas de otros colaboradores."
                            >
                              🔑 Restringido
                            </span>
                          )}
                        </div>

                        {editingSignatureUserId === user.id && canUpdateSignature(user) && (
                          <UserSignatureUpdater
                            userName={user.nombreCompleto}
                            currentSignature={user.firma}
                            onSave={(signatureBase64) => {
                              if (onUpdateUserSignature) {
                                onUpdateUserSignature(user.id, signatureBase64);
                              }
                              setEditingSignatureUserId(null);
                            }}
                            onCancel={() => setEditingSignatureUserId(null)}
                          />
                        )}
                      </div>
                    </div>
 
                   {/* Operational Footer Action */}
                   <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 min-h-[52px] flex items-center justify-between">
                     {deletingUserId === user.id ? (
                       <div className="flex items-center space-x-2 bg-rose-50 px-2 py-1.5 rounded-lg border border-rose-200 w-full justify-between animate-fade-in text-xs">
                         <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wider">¿Desea eliminar?</span>
                         <div className="flex space-x-1.5">
                           <button
                             onClick={() => {
                               onDeleteUser(user.id);
                               setDeletingUserId(null);
                             }}
                             className="bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-[10px] font-extrabold px-3 py-1 rounded-md cursor-pointer shadow-2xs transition-colors"
                           >
                             Sí, eliminar
                           </button>
                           <button
                             onClick={() => setDeletingUserId(null)}
                             className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-extrabold px-3 py-1 rounded-md cursor-pointer transition-colors"
                           >
                             No
                           </button>
                         </div>
                       </div>
                     ) : (
                       <>
                         <span className="text-[10px] text-slate-400 font-mono">
                           ID: #{user.id ? String(user.id).substring(Math.max(0, String(user.id).length - 4)) : 'N/A'}
                         </span>
 
                         <div className="flex items-center space-x-2">
                           {/* Delete User Button (only if not self) */}
                           {(currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad') && !isSelf && (
                             <button
                               onClick={() => setDeletingUserId(user.id)}
                               className="inline-flex items-center justify-center p-1.5 text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-200 hover:border-rose-600 rounded-lg transition-all cursor-pointer"
                               title="Eliminar usuario permanentemente"
                             >
                               <Trash2 className="h-3.5 w-3.5" />
                             </button>
                           )}

                           {/* Toggle Switch Operation */}
                           <button
                             onClick={() => handleToggleStatus(user)}
                             disabled={!!isSelf}
                             className={`${(currentUser?.username.toUpperCase() !== 'JUAN.VALENCIA' && currentUser?.cargo !== 'Director Técnico y de Calidad') ? 'hidden' : 'inline-flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-extrabold rounded-lg transition-all cursor-pointer'} ${
                               isSelf 
                                 ? 'bg-slate-100 text-slate-300 border border-slate-200 cursor-not-allowed'
                                 : isActive 
                                   ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200/40' 
                                   : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/40'
                             }`}
                             title={isActive ? 'Desactivar esta cuenta' : 'Activar esta cuenta'}
                           >
                             <Power className="h-3 w-3 shrink-0" />
                             <span>{isActive ? 'Suspender' : 'Habilitar'}</span>
                           </button>
                         </div>
                       </>
                     )}
                   </div>

                </div>
              );
            })}
          </div>
        )}

      </div>

    </div>
  );
}
